import type { PlayerInput, SeatId } from "@arcade-hockey/shared";
import { type GamepadSnapshot, KeyboardTracker, pollGamepads } from "@neoweb/input";
import {
  SeatInputMapper,
  type SeatSource,
  defaultGamepadSource,
  defaultP1KeyboardSource,
} from "../input/SeatInputMapper.js";

export class InputController {
  readonly keyboard = new KeyboardTracker();
  private mappers = new Map<SeatId, SeatInputMapper>();
  private padIndexBySeat = new Map<SeatId, number | null>();
  // Per-seat pooled PlayerInput — reused across ticks. Callers must consume
  // pollInputs' output within the frame; the same objects are overwritten
  // next tick.
  private readonly seatInputs = new Map<SeatId, PlayerInput>();
  private readonly outputBuffer: PlayerInput[] = [];

  constructor() {
    // P1 starts on keyboard; promotes to a gamepad as soon as one is plugged in.
    this.mappers.set("P1", new SeatInputMapper("P1", defaultP1KeyboardSource()));
    this.padIndexBySeat.set("P1", null);
  }

  attach(): void {
    this.keyboard.attach();
  }

  detach(): void {
    this.keyboard.detach();
  }

  private updateSeatSources(pads: GamepadSnapshot[]): void {
    // P1: prefer the lowest-indexed gamepad that is connected.
    const currentP1Index = this.padIndexBySeat.get("P1") ?? null;
    const firstPad = pads[0];
    if (firstPad !== undefined && firstPad.index !== currentP1Index) {
      this.padIndexBySeat.set("P1", firstPad.index);
      this.mappers.set("P1", new SeatInputMapper("P1", defaultGamepadSource(firstPad.index)));
    } else if (firstPad === undefined && currentP1Index !== null) {
      // Gamepad unplugged; fall back to keyboard.
      this.padIndexBySeat.set("P1", null);
      this.mappers.set("P1", new SeatInputMapper("P1", defaultP1KeyboardSource()));
    }
  }

  /**
   * Builds one PlayerInput per active seat for the given sim tick. Returns a
   * shared pooled array — callers must consume within the same frame.
   */
  pollInputs(tick: number): PlayerInput[] {
    const pads = pollGamepads();
    this.updateSeatSources(pads);

    this.outputBuffer.length = 0;
    for (const [seatId, mapper] of this.mappers) {
      let input = this.seatInputs.get(seatId);
      if (input === undefined) {
        input = {
          tick: 0,
          seq: 0,
          seatId,
          playerId: null,
          moveX: 0,
          moveY: 0,
          sprint: 0,
          buttons: 0,
        };
        this.seatInputs.set(seatId, input);
      }
      mapper.buildInputInto(input, { tick, keyboard: this.keyboard, gamepads: pads });
      // Rotate screen-space (right=+x, down=+y) move into sim-space (length=+x,
      // width=+y) in place. Inverse of Projection's world→screen rotation:
      //   worldX = -screenY, worldY = screenX
      const sx = input.moveX;
      const sy = input.moveY;
      input.moveX = -sy;
      input.moveY = sx;
      this.outputBuffer.push(input);
    }
    return this.outputBuffer;
  }

  setSeatSource(seat: SeatId, source: SeatSource): void {
    this.mappers.set(seat, new SeatInputMapper(seat, source));
  }
}
